import { useQuery } from "@tanstack/react-query";
import {
  UsersThreeIcon, DropIcon, KeyIcon, HardHatIcon, SealCheckIcon, WarningIcon, GraphIcon,
} from "@phosphor-icons/react";
import { fetchRugcheck, fetchDeployerFunding } from "../lib/api";
import { HitRateBadge, useDeployerStatsBatch } from "./HitRateBadge";

const GOOD = "#00E599", WARN = "#F0A020", BAD = "#FF3366", MUT = "#8A8F98";

const Row = ({ Icon, label, children, testid }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-white/5 last:border-0" data-testid={testid}>
    <Icon size={16} className="text-muted-foreground shrink-0 mt-0.5" />
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm mt-0.5 flex items-center gap-2 flex-wrap font-mono text-[12px]">{children}</div>
    </div>
  </div>
);

const Pill = ({ color, children }) => (
  <span className="px-1.5 py-0.5 font-bold text-[10px]" style={{ background: `${color}1a`, border: `1px solid ${color}`, color }}>
    {children}
  </span>
);

export const StructurePanel = ({ address }) => {
  const { data: rug } = useQuery({ queryKey: ["rugcheck", address], queryFn: () => fetchRugcheck(address), staleTime: 300000 });
  const creator = rug?.creator;
  const { data: fund } = useQuery({
    queryKey: ["deployerFunding", creator],
    queryFn: () => fetchDeployerFunding(creator),
    enabled: !!creator,
    staleTime: Infinity,
    retry: 1,
  });
  const dstats = useDeployerStatsBatch(creator ? [creator] : []);

  if (!rug?.available) return null;
  const b = rug.bundle || {};
  const th = rug.topHolderPct;
  const thColor = th == null ? MUT : th >= 20 ? BAD : th >= 10 ? WARN : GOOD;
  const lpColor = rug.lpUnlocked ? BAD : (rug.lpLockedPct || 0) >= 90 ? GOOD : (rug.lpLockedPct || 0) >= 50 ? WARN : BAD;
  const sevColor = { high: BAD, medium: WARN, low: WARN, clean: GOOD }[b.severity] || MUT;

  return (
    <div className="border p-5" style={{ borderColor: "rgba(0,229,153,0.25)", background: "rgba(0,229,153,0.03)" }} data-testid="structure-panel">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-heading font-bold text-lg">Structure</h2>
        <span className="font-mono text-[10px] text-muted-foreground">the fields that matter most</span>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2">Hard on-chain facts — read these before the composite score.</p>

      <Row Icon={UsersThreeIcon} label="Holder concentration" testid="structure-holders">
        <span style={{ color: thColor }}>top holder {th != null ? `${th}%` : "?"}</span>
        <span className="text-muted-foreground">· top 10 {rug.top10Pct != null ? `${rug.top10Pct}%` : "?"}</span>
        {b.detected ? (
          <Pill color={sevColor}><GraphIcon size={10} className="inline mr-0.5" />{b.insiderWallets} LINKED WALLETS · {String(b.severity).toUpperCase()}</Pill>
        ) : (
          <Pill color={GOOD}>NO LINKED-WALLET CLUSTER</Pill>
        )}
        {b.detected && b.heldPct > 0 && <span className="text-muted-foreground">cluster holds {b.heldPct}%</span>}
      </Row>

      <Row Icon={DropIcon} label="LP custody" testid="structure-lp">
        {rug.lpUnlocked ? (
          <Pill color={BAD}><WarningIcon size={10} className="inline mr-0.5" />LP UNLOCKED — can be pulled</Pill>
        ) : (
          <Pill color={lpColor}>{Math.round(rug.lpLockedPct || 0)}% LOCKED/BURNED</Pill>
        )}
        {rug.devSold === true && <span className="text-muted-foreground">dev balance sold</span>}
        {rug.devSold === false && rug.creatorPct > 0 && <span className="text-muted-foreground">dev still holds {rug.creatorPct}%</span>}
      </Row>

      <Row Icon={KeyIcon} label="Authorities" testid="structure-authorities">
        {rug.mintRevoked ? <Pill color={GOOD}><SealCheckIcon size={10} className="inline mr-0.5" />MINT REVOKED</Pill> : <Pill color={BAD}>MINT AUTHORITY LIVE</Pill>}
        {rug.freezeRevoked ? <Pill color={GOOD}>FREEZE REVOKED</Pill> : <Pill color={BAD}>FREEZE LIVE</Pill>}
        {rug.mutableMetadata && <Pill color={WARN}>METADATA MUTABLE</Pill>}
      </Row>

      <Row Icon={HardHatIcon} label="Deployer funding" testid="structure-funding">
        {creator && <HitRateBadge stats={dstats[creator]} compact />}
        {!creator ? (
          <span className="text-muted-foreground">deployer unknown</span>
        ) : !fund ? (
          <span className="text-muted-foreground">tracing funding…</span>
        ) : !fund.available || !fund.funder ? (
          <span className="text-muted-foreground">funding source not traceable</span>
        ) : (
          <>
            <span>funded by</span>
            <Pill color={fund.funderType === "cex" ? GOOD : fund.funderType === "deployer" ? BAD : WARN}>
              {(fund.funderLabel || "unknown").toUpperCase()}
            </Pill>
            {fund.amountSol != null && <span className="text-muted-foreground">{fund.amountSol} SOL initial</span>}
            {fund.funderDeployedTokens > 0 && <span style={{ color: BAD }}>funder deployed {fund.funderDeployedTokens} tokens itself</span>}
            {fund.siblingsFunded > 0 && <span style={{ color: WARN }}>same funder bankrolled {fund.siblingsFunded} other deployer{fund.siblingsFunded === 1 ? "" : "s"}</span>}
          </>
        )}
      </Row>
    </div>
  );
};
